import { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { HiTrash } from "react-icons/hi";
import { CiShoppingCart } from "react-icons/ci";
import {
  addToCart,
  removeFromCart,
  increaseQty,
  decreaseQty,
  clearCart,
} from "../../feature/cartSlice";
import product1 from "../../assets/Ashwagandha Botanical Supplement Showcase.png";

const BASE_API = (process.env.REACT_APP_BASE_API || "").replace(/\/+$/, "");

const formatPrice = (n) => `₹${Number(n).toLocaleString("en-IN")}`;

/* "abc.png" -> BASE/uploads/abc.png */
const toImageUrl = (src) => {
  if (!src) return product1;
  if (/^(https?:)?\/\//.test(src) || src.startsWith("data:")) return src;
  const clean = String(src).replace(/^\/+/, "");
  return clean.includes("/") ? `${BASE_API}/${clean}` : `${BASE_API}/uploads/${clean}`;
};

/* Shipping: set a flat fee here (0 shows "Free").
   Example: SHIPPING_FEE = 50 and FREE_SHIPPING_ABOVE = 999 */
const SHIPPING_FEE = 0;
const FREE_SHIPPING_ABOVE = null;

function Cart() {
  const items = useSelector((state) => state.cart.items);
  const loading = useSelector((state) => state.cart.loading);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  /* "Recommended for you" now comes from the API */
  const [recommended, setRecommended] = useState([]);

  useEffect(() => {
    
    const controller = new AbortController();

    const loadRecommended = async () => {
      try {
        const res = await fetch(`${BASE_API}/products`, {
          signal: controller.signal,
        });
        if (!res.ok) return;
        const rows = await res.json();
        setRecommended(
          (Array.isArray(rows) ? rows : []).map((p) => ({
            id: p.id,
            name: p.name ?? p.title,
            price: Number(p.price),
            image: toImageUrl(p.img ?? p.image),
          })),
        );
      } catch {
        /* recommendations are optional */
      }
    };

    loadRecommended();
    return () => controller.abort();
  }, []);

  const totalItems = items.reduce((sum, i) => sum + i.qty, 0);
  const subtotal = items.reduce((sum, i) => sum + i.price * i.qty, 0);

  // Discount only shows if cart items carry an `mrp` higher than their price
  const discount = items.reduce(
    (sum, i) => (i.mrp && i.mrp > i.price ? sum + (i.mrp - i.price) * i.qty : sum),
    0,
  );
  const mrpSubtotal = subtotal + discount;

  const shipping =
    FREE_SHIPPING_ABOVE !== null && subtotal >= FREE_SHIPPING_ABOVE ? 0 : SHIPPING_FEE;
  const total = subtotal + shipping;

  const suggestions = recommended
    .filter((r) => !items.some((i) => i.id === r.id))
    .slice(0, 3);

  // Guests can fill the cart, checkout needs a login
  const handleCheckout = (e) => {
    if (!localStorage.getItem("token")) {
      e.preventDefault();
      navigate("/signup", { state: { from: "/checkout" } }); // change "/login" if your route differs
    }
  };

  // Cart is still loading from the server: show the same empty page background, no "empty" flash
  if (loading && items.length === 0) {
    return <div className="min-h-[70vh] pt-[60px] bg-[#f6f8f2]" />;
  }

  // Empty state
  if (items.length === 0) {
    return (
      <div className="min-h-[70vh] pt-[60px] bg-[#f6f8f2] flex items-center justify-center px-4">
        <div className="text-center py-16">
          <CiShoppingCart className="mx-auto text-[80px] text-[#b9cdb4]" />
          <h2 className="mt-4 font-serif text-[22px] sm:text-[26px] text-[#1f4a2b]">
            Your cart is empty
          </h2>
          <p className="mt-2 text-[#6b7a6d] text-[14px]">
            Looks like you haven't added anything yet.
          </p>
          <Link
            to="/shop"
            className="inline-block mt-6 h-[44px] leading-[44px] px-8 bg-[#2f6b3a] hover:bg-[#1f4a2b] transition-colors text-white text-[13px] font-semibold rounded-md"
          >
            Continue Shopping
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-[60px] bg-[#f6f8f2] min-h-screen">
      <div className="w-full max-w-[1100px] mx-auto px-4 py-6 sm:py-10">
        {/* Heading */}
        <div className="flex items-end justify-between mb-5">
          <div>
            <h1 className="font-serif text-[24px] sm:text-[30px] text-[#1f2d22]">
              Your Cart <span className="text-[#6b7a6d]">({totalItems})</span>
            </h1>
          </div>
          <button
            onClick={() => dispatch(clearCart())}
            className="text-[13px] text-[#6b7a6d] hover:text-[#d9534f] underline"
          >
            Clear cart
          </button>
        </div>

        <div className="flex flex-col lg:flex-row gap-6 lg:gap-8 items-start">
          {/* Left: items + price details */}
          <div className="w-full lg:flex-1 min-w-0 flex flex-col gap-4">
            <div className="bg-white border border-[#e3e9dd] rounded-lg divide-y divide-[#e3e9dd]">
              {items.map((item) => (
                <div
                  key={`${item.id}-${item.size ?? ""}`}
                  className="p-3 sm:p-5 flex gap-3 sm:gap-5"
                >
                  <img
                    src={item.image}
                    alt={item.name}
                    className="w-[84px] h-[84px] sm:w-[104px] sm:h-[104px] object-cover rounded-md bg-[#f4f7ef] shrink-0"
                  />

                  <div className="flex-1 min-w-0 flex flex-col justify-between">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <h4 className="text-[14px] sm:text-[16px] font-semibold text-[#1f2d22] leading-snug line-clamp-2">
                          {item.name}
                        </h4>
                        {item.size && (
                          <p className="mt-0.5 text-[12px] sm:text-[13px] text-[#6b7a6d]">
                            Size: <span className="text-[#1f2d22]">{item.size}</span>
                          </p>
                        )}
                        <p className="mt-1 text-[13px] sm:text-[14px] text-[#4b574d]">
                          {formatPrice(item.price)}
                        </p>
                      </div>

                      <button
                        onClick={() => dispatch(removeFromCart(item))}
                        className="p-1.5 text-[#9aa79c] hover:text-[#d9534f] transition-colors"
                        aria-label="Remove item"
                      >
                        <HiTrash className="text-[18px]" />
                      </button>
                    </div>

                    <div className="flex items-center justify-between mt-3">
                      <div className="flex items-center border border-[#d6e0d0] rounded-md overflow-hidden">
                        <button
                          onClick={() => dispatch(decreaseQty(item))}
                          disabled={item.qty <= 1}
                          className="w-[32px] h-[32px] sm:w-[36px] sm:h-[36px] text-[17px] text-[#1f4a2b] hover:bg-[#eef5ea] disabled:opacity-40 disabled:cursor-not-allowed"
                          aria-label="Decrease quantity"
                        >
                          -
                        </button>
                        <span className="w-[34px] sm:w-[40px] h-[32px] sm:h-[36px] flex items-center justify-center border-x border-[#d6e0d0] text-[13px]">
                          {item.qty}
                        </span>
                        <button
                          onClick={() => dispatch(increaseQty(item))}
                          disabled={item.stock != null && item.qty >= item.stock}
                          className="w-[32px] h-[32px] sm:w-[36px] sm:h-[36px] text-[17px] text-[#1f4a2b] hover:bg-[#eef5ea]"
                          aria-label="Increase quantity"
                        >
                          +
                        </button>
                      </div>

                      <p className="text-[15px] sm:text-[17px] font-bold text-[#1f2d22]">
                        {formatPrice(item.price * item.qty)}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Price details */}
            <div className="bg-white border border-[#e3e9dd] rounded-lg p-5 sm:p-6">
              <h3 className="text-[16px] sm:text-[18px] font-semibold text-[#1f4a2b] mb-3">
                Price Details
              </h3>

              <div className="flex justify-between text-[14px] py-2">
                <span className="text-[#4b574d]">Subtotal ({totalItems} items)</span>
                <span>{formatPrice(mrpSubtotal)}</span>
              </div>
              {discount > 0 && (
                <div className="flex justify-between text-[14px] py-2">
                  <span className="text-[#4b574d]">Discount</span>
                  <span className="text-[#2f6b3a]">-{formatPrice(discount)}</span>
                </div>
              )}
              <div className="flex justify-between text-[14px] py-2">
                <span className="text-[#4b574d]">Shipping</span>
                <span className={shipping === 0 ? "text-[#2f6b3a]" : ""}>
                  {shipping === 0 ? "Free" : formatPrice(shipping)}
                </span>
              </div>

              <div className="flex justify-between text-[17px] sm:text-[19px] font-bold text-[#1f2d22] border-t border-[#e3e9dd] mt-3 pt-4">
                <span>Total</span>
                <span>{formatPrice(total)}</span>
              </div>

              <Link
                to="/checkout"
                onClick={handleCheckout}
                className="block w-full mt-5 h-[46px] leading-[46px] text-center bg-[#2f6b3a] hover:bg-[#1f4a2b] transition-colors text-white text-[14px] font-semibold rounded-md"
              >
                Proceed to Checkout
              </Link>
              <Link
                to="/shop"
                className="block w-full mt-3 h-[44px] leading-[42px] text-center border border-[#2f6b3a] text-[#2f6b3a] hover:bg-[#eef5ea] transition-colors text-[13px] font-semibold rounded-md"
              >
                Continue Shopping
              </Link>

              <p className="mt-3 text-center text-[12px] text-[#9aa79c]">
                Taxes included where applicable
              </p>
            </div>
          </div>

          {/* Right: recommended */}
          {suggestions.length > 0 && (
            <aside className="w-full lg:w-[300px] shrink-0 bg-white border border-[#e3e9dd] rounded-lg p-5">
              <h3 className="text-[16px] font-semibold text-[#1f4a2b] mb-4">
                Recommended for you
              </h3>
              <div className="flex flex-col gap-4">
                {suggestions.map((r) => (
                  <div key={r.id} className="flex items-center gap-3">
                    <Link to={`/product/${r.id}`} className="shrink-0">
                      <img
                        src={r.image}
                        alt={r.name}
                        className="w-[60px] h-[60px] object-cover rounded-md bg-[#f4f7ef]"
                      />
                    </Link>
                    <div className="flex-1 min-w-0">
                      <Link
                        to={`/product/${r.id}`}
                        className="block text-[13px] font-semibold text-[#1f2d22] leading-snug hover:text-[#2f6b3a]"
                      >
                        {r.name}
                      </Link>
                      <p className="text-[13px] text-[#4b574d] mt-0.5">
                        {formatPrice(r.price)}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() =>
                        dispatch(
                          addToCart({
                            id: r.id,
                            name: r.name,
                            price: r.price,
                            image: r.image,
                            qty: 1,
                          }),
                        )
                      }
                      className="px-3 h-[30px] bg-[#2f6b3a] hover:bg-[#1f4a2b] transition-colors text-white text-[12px] font-semibold rounded-md"
                    >
                      Add
                    </button>
                  </div>
                ))}
              </div>
            </aside>
          )}
        </div>
      </div>
    </div>
  );
}

export default Cart;
